import React, { useCallback, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { getStroke } from 'perfect-freehand';
import {
  DrawingData,
  DrawingPoint,
  DrawingStroke,
  DrawingTool,
  exportAsAnimationData,
  exportAsPNGWithTransparency,
  getSvgPathFromStroke,
} from '../utils/drawingExport';

interface SVGDrawingBoardProps {
  width: number;
  height: number;
  currentColor: string;
  strokeWidth: number;
  currentTool: DrawingTool;
  onCanUndoChange: (canUndo: boolean) => void;
  onCanRedoChange?: (canRedo: boolean) => void;
  onSave: (blob: Blob) => void;
  onPersist?: (data: DrawingData | null) => void;
  onStrokeCountChange?: (count: number) => void;
}

export interface SVGDrawingBoardHandle {
  handleSave: () => Promise<boolean>;
  clear: () => void;
  handleUndo: () => void;
  handleRedo: () => void;
  getDrawingData: () => DrawingData | null;
  loadDrawingData: (data: DrawingData) => void;
  setBackgroundImage: (dataUrl: string | null) => void;
}

const MIN_PRESSURE = 0.5;

const buildStrokePath = (stroke: DrawingStroke): string => {
  if (!stroke.points.length) return '';
  const points = stroke.points.map((point) => [point.x, point.y, point.pressure ?? MIN_PRESSURE]);
  const outline = getStroke(points, {
    size: stroke.size,
    thinning: 0.5,
    smoothing: 0.5,
    streamline: 0.5,
    simulatePressure: stroke.points.every((point) => point.pressure == null),
  });
  return getSvgPathFromStroke(outline);
};

// Committed strokes are immutable, so memoizing on the stroke reference
// avoids recomputing every outline on each pointer move.
const StrokePath = React.memo(({ stroke }: { stroke: DrawingStroke }) => {
  const path = useMemo(() => buildStrokePath(stroke), [stroke]);
  if (!path) return null;
  return <path d={path} fill={stroke.tool === 'eraser' ? '#ffffff' : stroke.color} />;
});

StrokePath.displayName = 'StrokePath';

const SVGDrawingBoard = forwardRef<SVGDrawingBoardHandle, SVGDrawingBoardProps>(
  ({ width, height, currentColor, strokeWidth, currentTool, onCanUndoChange, onCanRedoChange, onSave, onPersist, onStrokeCountChange }, ref) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const viewBoxRef = useRef({ width, height });
    const [strokes, setStrokes] = useState<DrawingStroke[]>([]);
    const [redoStack, setRedoStack] = useState<DrawingStroke[]>([]);
    const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
    const currentStrokeRef = useRef<DrawingPoint[]>([]);
    const currentStrokeMetaRef = useRef<{ tool: DrawingTool; color: string; size: number } | null>(null);
    const [renderTick, setRenderTick] = useState(0);
    const strokeCountRef = useRef(0);
    const backgroundImageRef = useRef<string | null>(null);
    const activePointerIdRef = useRef<number | null>(null);
    const activeTouchIdRef = useRef<number | null>(null);
    const activeInputRef = useRef<'pointer' | 'touch' | null>(null);
    const isDrawingRef = useRef(false);

    useEffect(() => {
      if (strokes.length === 0 && currentStrokeRef.current.length === 0) {
        viewBoxRef.current = { width, height };
      }
    }, [width, height, strokes.length]);

    useEffect(() => {
      onCanUndoChange(strokes.length > 0);
    }, [strokes.length, onCanUndoChange]);

    useEffect(() => {
      onCanRedoChange?.(redoStack.length > 0);
    }, [onCanRedoChange, redoStack.length]);

    const normalizePressure = (pressure: number) => (pressure > 0 ? pressure : MIN_PRESSURE);

    const getViewBoxCoords = useCallback((clientX: number, clientY: number) => {
      const svg = svgRef.current;
      const viewBox = viewBoxRef.current;
      if (!svg) {
        return { x: clientX, y: clientY };
      }

      const point = svg.createSVGPoint?.();
      const ctm = svg.getScreenCTM?.();
      if (point && ctm) {
        point.x = clientX;
        point.y = clientY;
        const svgPoint = point.matrixTransform(ctm.inverse());
        return {
          x: Math.min(viewBox.width, Math.max(0, svgPoint.x)),
          y: Math.min(viewBox.height, Math.max(0, svgPoint.y)),
        };
      }

      const rect = svg.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * viewBox.width;
      const y = ((clientY - rect.top) / rect.height) * viewBox.height;
      return {
        x: Math.min(viewBox.width, Math.max(0, x)),
        y: Math.min(viewBox.height, Math.max(0, y)),
      };
    }, []);

    const buildDrawingData = useCallback(
      (nextStrokes: DrawingStroke[], nextBackground?: string | null) => {
        const backgroundDataUrl =
          typeof nextBackground === 'string' ? nextBackground : backgroundImageRef.current || undefined;
        if (!nextStrokes.length && !backgroundDataUrl) return null;
        return exportAsAnimationData(
          nextStrokes,
          viewBoxRef.current.width,
          viewBoxRef.current.height,
          backgroundDataUrl
        );
      },
      []
    );

    const getAllStrokes = useCallback(() => {
      const currentStroke = currentStrokeRef.current;
      const meta = currentStrokeMetaRef.current;
      if (!currentStroke.length || !meta) {
        return strokes;
      }
      return [
        ...strokes,
        {
          tool: meta.tool,
          points: [...currentStroke],
          color: meta.color,
          size: meta.size,
        },
      ];
    }, [strokes]);

    const commitCurrentStroke = useCallback(() => {
      const points = currentStrokeRef.current;
      const meta = currentStrokeMetaRef.current;
      if (!points.length || !meta) {
        currentStrokeRef.current = [];
        currentStrokeMetaRef.current = null;
        return null;
      }

      const stroke: DrawingStroke = {
        tool: meta.tool,
        points: [...points],
        color: meta.color,
        size: meta.size,
      };

      currentStrokeRef.current = [];
      currentStrokeMetaRef.current = null;

      setStrokes((prev) => [...prev, stroke]);
      setRedoStack([]);
      const nextCount = strokeCountRef.current + 1;
      strokeCountRef.current = nextCount;
      onStrokeCountChange?.(nextCount);
      onPersist?.(buildDrawingData([...strokes, stroke]));
      return stroke;
    }, [buildDrawingData, onPersist, onStrokeCountChange, strokes]);

    const appendPoint = useCallback(
      (clientX: number, clientY: number, pressure: number) => {
        if (!currentStrokeRef.current.length) return;
        const { x, y } = getViewBoxCoords(clientX, clientY);
        currentStrokeRef.current.push({
          x,
          y,
          pressure: normalizePressure(pressure),
          timestamp: Date.now(),
        });
        setRenderTick((tick) => tick + 1);
      },
      [getViewBoxCoords]
    );

    const finalizePointerStroke = useCallback(
      (pointerId?: number) => {
        if (activeInputRef.current !== 'pointer') return;
        if (pointerId != null && activePointerIdRef.current != null && pointerId !== activePointerIdRef.current) {
          return;
        }
        if (!currentStrokeRef.current.length) return;
        if (pointerId != null) {
          try {
            svgRef.current?.releasePointerCapture(pointerId);
          } catch {
            // Ignore if pointer was not captured.
          }
        }
        activePointerIdRef.current = null;
        activeInputRef.current = null;
        isDrawingRef.current = false;
        commitCurrentStroke();
      },
      [commitCurrentStroke]
    );

    const finalizeTouchStroke = useCallback(() => {
      if (activeInputRef.current !== 'touch') return;
      if (!currentStrokeRef.current.length) return;
      activeTouchIdRef.current = null;
      activeInputRef.current = null;
      isDrawingRef.current = false;
      commitCurrentStroke();
    }, [commitCurrentStroke]);

    useEffect(() => {
      const handleWindowPointerMove = (event: PointerEvent) => {
        if (activeInputRef.current !== 'pointer') return;
        if (!isDrawingRef.current) return;
        if (activePointerIdRef.current != null && event.pointerId !== activePointerIdRef.current) return;
        event.preventDefault();
        appendPoint(event.clientX, event.clientY, event.pressure);
      };

      const handleWindowPointerEnd = (event: PointerEvent) => {
        if (activeInputRef.current !== 'pointer') return;
        if (!isDrawingRef.current) return;
        if (activePointerIdRef.current != null && event.pointerId !== activePointerIdRef.current) return;
        event.preventDefault();
        finalizePointerStroke(event.pointerId);
      };

      window.addEventListener('pointermove', handleWindowPointerMove, { passive: false });
      window.addEventListener('pointerup', handleWindowPointerEnd, { passive: false });
      window.addEventListener('pointercancel', handleWindowPointerEnd, { passive: false });

      return () => {
        window.removeEventListener('pointermove', handleWindowPointerMove);
        window.removeEventListener('pointerup', handleWindowPointerEnd);
        window.removeEventListener('pointercancel', handleWindowPointerEnd);
      };
    }, [appendPoint, finalizePointerStroke]);

    const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
      if (event.pointerType === 'touch') return;
      if (activeInputRef.current && activeInputRef.current !== 'pointer') return;
      if (event.button !== 0) return;
      event.preventDefault();
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Ignore if pointer capture is not supported.
      }

      activeInputRef.current = 'pointer';
      activePointerIdRef.current = event.pointerId;
      isDrawingRef.current = true;
      const { x, y } = getViewBoxCoords(event.clientX, event.clientY);
      currentStrokeMetaRef.current = {
        tool: currentTool,
        color: currentTool === 'eraser' ? '#ffffff' : currentColor,
        size: strokeWidth,
      };
      currentStrokeRef.current = [
        {
          x,
          y,
          pressure: normalizePressure(event.pressure),
          timestamp: Date.now(),
        },
      ];
      setRenderTick((tick) => tick + 1);
    };

    const handlePointerCancel = (event: React.PointerEvent<SVGSVGElement>) => {
      event.preventDefault();
      finalizePointerStroke(event.pointerId);
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (activeInputRef.current && activeInputRef.current !== 'touch') return;
      if (event.touches.length > 1) {
        event.preventDefault();
        return;
      }
      const touch = event.changedTouches[0];
      if (!touch) return;
      event.preventDefault();
      activeInputRef.current = 'touch';
      activeTouchIdRef.current = touch.identifier;
      isDrawingRef.current = true;

      const { x, y } = getViewBoxCoords(touch.clientX, touch.clientY);
      currentStrokeMetaRef.current = {
        tool: currentTool,
        color: currentTool === 'eraser' ? '#ffffff' : currentColor,
        size: strokeWidth,
      };
      currentStrokeRef.current = [
        {
          x,
          y,
          pressure: MIN_PRESSURE,
          timestamp: Date.now(),
        },
      ];
      setRenderTick((tick) => tick + 1);
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (activeInputRef.current !== 'touch') return;
      if (event.touches.length > 1) {
        event.preventDefault();
        return;
      }
      const touchId = activeTouchIdRef.current;
      if (touchId == null) return;
      const touch = Array.from(event.touches).find((item) => item.identifier === touchId);
      if (!touch) return;
      event.preventDefault();
      appendPoint(touch.clientX, touch.clientY, MIN_PRESSURE);
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (activeInputRef.current !== 'touch') return;
      const touchId = activeTouchIdRef.current;
      if (touchId == null) return;
      const ended = Array.from(event.changedTouches).some((item) => item.identifier === touchId);
      if (!ended) return;
      event.preventDefault();
      finalizeTouchStroke();
    };

    // Touch listeners must be attached natively with passive: false —
    // React's synthetic touchstart/touchmove are passive, so their
    // preventDefault() is ignored and iOS scrolls the page mid-stroke.
    const touchHandlersRef = useRef({ start: handleTouchStart, move: handleTouchMove, end: handleTouchEnd });
    touchHandlersRef.current = { start: handleTouchStart, move: handleTouchMove, end: handleTouchEnd };

    useEffect(() => {
      const svg = svgRef.current;
      if (!svg) return;

      const onTouchStart = (event: TouchEvent) => touchHandlersRef.current.start(event);
      const onTouchMove = (event: TouchEvent) => touchHandlersRef.current.move(event);
      const onTouchEnd = (event: TouchEvent) => touchHandlersRef.current.end(event);

      svg.addEventListener('touchstart', onTouchStart, { passive: false });
      svg.addEventListener('touchmove', onTouchMove, { passive: false });
      svg.addEventListener('touchend', onTouchEnd, { passive: false });
      svg.addEventListener('touchcancel', onTouchEnd, { passive: false });

      // Backstop while a stroke is in flight: even if the touch wanders off
      // the canvas (or iOS ignores touch-action because the page is zoomed),
      // the page must not scroll.
      const blockScrollWhileDrawing = (event: TouchEvent) => {
        if (isDrawingRef.current && event.cancelable) {
          event.preventDefault();
        }
      };
      window.addEventListener('touchmove', blockScrollWhileDrawing, { passive: false });

      return () => {
        svg.removeEventListener('touchstart', onTouchStart);
        svg.removeEventListener('touchmove', onTouchMove);
        svg.removeEventListener('touchend', onTouchEnd);
        svg.removeEventListener('touchcancel', onTouchEnd);
        window.removeEventListener('touchmove', blockScrollWhileDrawing);
      };
    }, []);

    const currentStrokePath = useMemo(() => {
      const points = currentStrokeRef.current;
      const meta = currentStrokeMetaRef.current;
      if (!points.length || !meta) return '';
      const outline = getStroke(
        points.map((point) => [point.x, point.y, point.pressure ?? MIN_PRESSURE]),
        {
          size: meta.size,
          thinning: 0.5,
          smoothing: 0.5,
          streamline: 0.5,
          simulatePressure: points.every((point) => point.pressure == null),
        }
      );
      return getSvgPathFromStroke(outline);
    }, [currentTool, currentColor, strokeWidth, renderTick, strokes.length]);

    useImperativeHandle(ref, () => ({
      // Resolves true when the export succeeded and onSave was invoked;
      // false when there was nothing to save or the export failed.
      handleSave: () => {
        const strokesToSave = getAllStrokes();
        const backgroundDataUrl = backgroundImageRef.current || undefined;
        if (!strokesToSave.length && !backgroundDataUrl) return Promise.resolve(false);
        return exportAsPNGWithTransparency(
          strokesToSave,
          viewBoxRef.current.width,
          viewBoxRef.current.height,
          backgroundDataUrl
        )
          .then((blob) => {
            onSave(blob);
            return true;
          })
          .catch((error) => {
            console.error('Failed to export PNG', error);
            return false;
          });
      },
      clear: () => {
        currentStrokeRef.current = [];
        currentStrokeMetaRef.current = null;
        activePointerIdRef.current = null;
        activeTouchIdRef.current = null;
        activeInputRef.current = null;
        isDrawingRef.current = false;
        setStrokes([]);
        setRedoStack([]);
        setBackgroundImage(null);
        backgroundImageRef.current = null;
        strokeCountRef.current = 0;
        onStrokeCountChange?.(0);
        onPersist?.(null);
      },
      handleUndo: () => {
        if (isDrawingRef.current) {
          currentStrokeRef.current = [];
          currentStrokeMetaRef.current = null;
          activePointerIdRef.current = null;
          activeTouchIdRef.current = null;
          activeInputRef.current = null;
          isDrawingRef.current = false;
          setRenderTick((tick) => tick + 1);
          return;
        }

        setStrokes((prev) => {
          if (!prev.length) return prev;
          const next = prev.slice(0, -1);
          const undone = prev[prev.length - 1];
          setRedoStack((redoPrev) => [undone, ...redoPrev]);
          const nextCount = Math.max(0, strokeCountRef.current - 1);
          strokeCountRef.current = nextCount;
          onStrokeCountChange?.(nextCount);
          onPersist?.(buildDrawingData(next));
          return next;
        });
      },
      handleRedo: () => {
        if (isDrawingRef.current) return;
        setRedoStack((prev) => {
          if (!prev.length) return prev;
          const [redoStroke, ...rest] = prev;
          setStrokes((strokesPrev) => {
            const next = [...strokesPrev, redoStroke];
            const nextCount = strokeCountRef.current + 1;
            strokeCountRef.current = nextCount;
            onStrokeCountChange?.(nextCount);
            onPersist?.(buildDrawingData(next));
            return next;
          });
          return rest;
        });
      },
      getDrawingData: () => {
        return buildDrawingData(getAllStrokes());
      },
      loadDrawingData: (data: DrawingData) => {
        currentStrokeRef.current = [];
        currentStrokeMetaRef.current = null;
        activePointerIdRef.current = null;
        activeTouchIdRef.current = null;
        activeInputRef.current = null;
        isDrawingRef.current = false;
        viewBoxRef.current = { width: data.viewBox.width, height: data.viewBox.height };
        setBackgroundImage(data.backgroundDataUrl ?? null);
        backgroundImageRef.current = data.backgroundDataUrl ?? null;
        setStrokes(data.strokes);
        setRedoStack([]);
        strokeCountRef.current = data.strokes.length;
        onStrokeCountChange?.(data.strokes.length);
      },
      setBackgroundImage: (dataUrl: string | null) => {
        backgroundImageRef.current = dataUrl;
        setBackgroundImage(dataUrl);
        onPersist?.(buildDrawingData(getAllStrokes(), dataUrl));
      },
    }));

    const viewBox = viewBoxRef.current;

    return (
      // Wrapper carries touch-action too: iOS WebKit doesn't reliably honor
      // touch-action set directly on an SVG root.
      <div style={{ width: '100%', height: '100%', touchAction: 'none' }}>
        <svg
          ref={svgRef}
        width={width}
        height={height}
        viewBox={`0 0 ${viewBox.width} ${viewBox.height}`}
        onPointerDown={handlePointerDown}
        onPointerCancel={handlePointerCancel}
        className="border border-gray-300 rounded-lg cursor-crosshair"
        style={{
          width: '100%',
          height: '100%',
          maxWidth: `${width}px`,
          maxHeight: `${height}px`,
          background: '#ffffff',
          touchAction: 'none',
          overscrollBehavior: 'contain',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {backgroundImage ? (
          <image
            href={backgroundImage}
            x="0"
            y="0"
            width={viewBox.width}
            height={viewBox.height}
            preserveAspectRatio="xMidYMid slice"
            pointerEvents="none"
          />
        ) : null}
        {strokes.map((stroke, index) => (
          <StrokePath key={`${index}-${stroke.points.length}`} stroke={stroke} />
        ))}
        {currentStrokePath ? (
          <path
            d={currentStrokePath}
            fill={currentTool === 'eraser' ? '#ffffff' : currentColor}
          />
        ) : null}
        </svg>
      </div>
    );
  }
);

SVGDrawingBoard.displayName = 'SVGDrawingBoard';

// The parent re-renders on every poll refetch; identical props must not
// cascade into the drawing surface mid-stroke
export default React.memo(SVGDrawingBoard);
